import { createApiClient, apiClient } from '../../../../api/apiClient';
//import { ReportCategory } from '../../../AnalyticsHub/pages/Reports/reportCatalog';
import { ReportConfigRow, ReportStatus, ReportType } from '../../../../store/slices/reportingConfigurationSlice';

const API_BASE = '/admin/reports/config';
const API_BASE_COUNT = '/admin/reports';
const KPI_TAGS_BASE = '/admin/reports/tags/kpi';
const AUDIT_BASE = '/admin/reports/config/audit';
const BULK_BASE = '/admin/reports/config/bulk';

const api = createApiClient();

const LOCAL_REPORT_SEEDS: ReportConfigRow[] = [
  {
    id: 'order-rules-local-seed',
    reportId: null,
    name: 'Order Rules',
    primaryTag: 'Accuracy',
    secondaryTags: ['Navigator'],
    kpiTags: ['order rules', 'recommendation acceptance', 'max potential'],
    status: 'Active',
    description: 'In-app Order Rules analytics with execution, recommendations, and max potential insights.',
    link: '/navigator-reports/order-rules',
    icon: 'rule',
    reportType: 'INTERNAL_REDIRECT',
  },
];

function ensureSeedRows(rows: ReportConfigRow[]): ReportConfigRow[] {
  const existingLinks = new Set(rows.map((row) => String(row.link ?? '').trim().toLowerCase()));
  const nextRows = [...rows];

  LOCAL_REPORT_SEEDS.forEach((seed) => {
    const normalizedLink = String(seed.link ?? '').trim().toLowerCase();
    if (!existingLinks.has(normalizedLink)) {
      nextRows.push(seed);
      existingLinks.add(normalizedLink);
    }
  });

  return nextRows;
}

export interface ReportingConfigurationMutationContext {
  userEmail: string;
  gpid: string;
}

export interface ReportingConfigurationUpsertInput {
  reportId: number | null;
  name: string;
  primaryTag: string;
  secondaryTags: string[];
  kpiTags: string[];
  status: ReportStatus;
  description: string;
  link: string;
  icon: string;
}

export interface ReportingConfigurationStatusInput extends ReportingConfigurationMutationContext {
  reportId: number;
  status: ReportStatus;
}

export interface ReportingConfigurationBulkStatusInput extends ReportingConfigurationMutationContext {
  updates: Array<{
    reportId: number;
    status: ReportStatus;
  }>;
}

function asArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item).trim();
        }

        return pickString(
          (item as any)?.kpiTag,
          (item as any)?.tag,
          (item as any)?.name,
          (item as any)?.value,
          (item as any)?.label
        );
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) {
      return [];
    }

    if (raw.startsWith('[') && raw.endsWith(']')) {
      try {
        return asArray(JSON.parse(raw));
      } catch {
        // Fall back to comma-split when payload is not valid JSON.
      }
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeStatus(value: unknown): ReportStatus {
  if (typeof value === 'boolean') {
    return value ? 'Active' : 'Disabled';
  }

  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'disabled' || normalized === 'inactive' || normalized === 'false' || normalized === '0'
    ? 'Disabled'
    : 'Active';
}

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }

  return '';
}

function pickNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }

  return null;
}

function unwrapResponse<T>(data: any): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }

  if (Array.isArray(data?.data)) {
    return data.data as T[];
  }

  if (Array.isArray(data?.content)) {
    return data.content as T[];
  }

  if (Array.isArray(data?.items)) {
    return data.items as T[];
  }

  return [];
}

export function mapApiToReportConfigRow(raw: any, index = 0): ReportConfigRow {
  const reportId = pickNumber(raw?.reportId, raw?.id, raw?.reportID, raw?.report_id);
  const fallbackId = pickString(raw?.id, raw?.reportId, raw?.reportID, raw?.report_id, `report-${index}`);
  const secondaryTags = asArray(raw?.secondaryTags ?? raw?.secondaryTag ?? raw?.categories?.slice?.(1));
  const kpiTags = asArray(raw?.kpiTags ?? raw?.kpiTag ?? raw?.kpis);

  const reportTypeRaw = pickString(raw?.reportType, raw?.report_type);
  const reportType: ReportType = reportTypeRaw === 'INTERNAL_REDIRECT' ? 'INTERNAL_REDIRECT' : 'EXTERNAL';

  return {
    id: fallbackId,
    reportId,
    name: pickString(raw?.name, raw?.reportName, raw?.report_title, raw?.title),
    primaryTag: pickString(raw?.primaryTag, raw?.primary_tag, raw?.category, raw?.primaryCategory),
    secondaryTags: secondaryTags,
    kpiTags,
    status: normalizeStatus(raw?.status ?? raw?.isActive ?? raw?.active),
    description: pickString(raw?.description, raw?.reportDescription, raw?.reportDesc, raw?.summary),
    link: pickString(raw?.link, raw?.reportLink, raw?.reportUrl, raw?.url),
    icon: pickString(raw?.icon, raw?.materialIcon, raw?.iconName, 'analytics'),
    reportType,
  };
}

function buildMutationBody(input: ReportingConfigurationUpsertInput, userEmail: string, gpid: string) {
  const icon = pickString(input.icon, 'analytics');

  return {
    name: input.name,
    reportName: input.name,
    primaryTag: input.primaryTag,
    secondaryTags: input.secondaryTags,
    kpiTags: input.kpiTags,
    status: input.status,
    description: input.description,
    reportDescription: input.description,
    link: input.link,
    reportLink: input.link,
    reportUrl: input.link,
    icon,
    iconName: icon,
    userEmail,
    gpid,
  };
}

export const reportingConfigurationApi = {
  getCounts: async (): Promise<{ totalCount: number; enabledCount: number; disabledCount: number }> => {
    try {
      const data = await apiClient(api, `${API_BASE_COUNT}/count`, 'GET');
      const payload = data?.data ?? data;
      return {
        totalCount: pickNumber(payload?.totalCount, payload?.totalcount, payload?.total) ?? 0,
        enabledCount: pickNumber(payload?.enabledCount, payload?.enabledCount, payload?.enabled) ?? 0,
        disabledCount: pickNumber(payload?.disabledCount, payload?.disabledCount, payload?.disabled) ?? 0,
      };
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to load report counts');
    }
  },
  getAll: async (): Promise<ReportConfigRow[]> => {
    try {
      const data = await apiClient(api, API_BASE, 'GET');
      const rows = unwrapResponse<any>(data);
      const mapped = rows.map((row, index) => mapApiToReportConfigRow(row, index));
      return ensureSeedRows(mapped);
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to load report configurations');
    }
  },

  getKpiTags: async (): Promise<string[]> => {
    try {
      const data = await apiClient(api, KPI_TAGS_BASE, 'GET');
      const rows = unwrapResponse<any>(data);
      const tags = rows.length > 0 ? rows : asArray(data?.data ?? data?.tags ?? data);

      const normalizedTags = tags
        .map((tag) => {
          if (typeof tag === 'string' || typeof tag === 'number') {
            return String(tag).trim();
          }

          return pickString(
            tag?.kpiTag,
            tag?.kpi,
            tag?.tag,
            tag?.name,
            tag?.value
          );
        })
        .filter(Boolean);

      return Array.from(new Set(normalizedTags)).sort((a, b) => a.localeCompare(b));
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to load KPI tags');
    }
  },

  getAuditTrail: async (reportId: number): Promise<any[]> => {
    try {
      const params = new URLSearchParams({ reportId: String(reportId) });
      const data = await apiClient(api, `${AUDIT_BASE}?${params.toString()}`, 'GET');
      return unwrapResponse<any>(data);
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to load report audit trail');
    }
  },

  create: async (
    input: ReportingConfigurationUpsertInput,
    context: ReportingConfigurationMutationContext
  ): Promise<ReportConfigRow> => {
    try {
      const body = buildMutationBody(input, context.userEmail, context.gpid);
      const data = await apiClient(api, API_BASE, 'POST', body);
      const row = unwrapResponse<any>(data)[0] ?? data?.data ?? data;
      return mapApiToReportConfigRow(row);
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to create report configuration');
    }
  },

  update: async (
    reportId: number,
    input: ReportingConfigurationUpsertInput,
    context: ReportingConfigurationMutationContext
  ): Promise<ReportConfigRow> => {
    try {
      const body = buildMutationBody({ ...input, reportId }, context.userEmail, context.gpid);
      const data = await apiClient(api, `${API_BASE}/${reportId}`, 'PUT', body);
      const row = unwrapResponse<any>(data)[0] ?? data?.data ?? data;
      return mapApiToReportConfigRow(row, 0);
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to update report configuration');
    }
  },

  updateStatus: async ({ reportId, status, userEmail, gpid }: ReportingConfigurationStatusInput): Promise<ReportConfigRow> => {
    try {
      const data = await apiClient(api, `${API_BASE}/${reportId}/status`, 'PATCH', {
        reportId,
        status,
        userEmail,
        gpid,
        updatedByEmail: userEmail,
        updatedByGpid: gpid,
      });
      const row = unwrapResponse<any>(data)[0] ?? data?.data ?? data;
      return mapApiToReportConfigRow(row, 0);
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to update report status');
    }
  },

  updateBulkStatus: async ({ updates, userEmail, gpid }: ReportingConfigurationBulkStatusInput): Promise<ReportConfigRow[]> => {
    try {
      const data = await apiClient(api, BULK_BASE, 'PATCH', {
        updates: updates.map((update) => ({
          reportId: update.reportId,
          status: update.status,
          userEmail,
          gpid,
        })),
        userEmail,
        gpid,
      });
      return unwrapResponse<any>(data).map((row, index) => mapApiToReportConfigRow(row, index));
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to bulk update report status');
    }
  },

  delete: async (reportId: number): Promise<void> => {
    try {
      await apiClient(api, `${API_BASE}/${reportId}`, 'DELETE');
    } catch (error: any) {
      throw new Error(error?.response?.data?.message ?? error?.message ?? 'Failed to delete report configuration');
    }
  },

  softDelete: async (
    reportId: number,
    context: ReportingConfigurationMutationContext
  ): Promise<ReportConfigRow> => {
    return reportingConfigurationApi.updateStatus({
      reportId,
      status: 'Disabled',
      userEmail: context.userEmail,
      gpid: context.gpid,
    });
  },
};

export default reportingConfigurationApi;
